import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import { DiscussButton, ExploreButton } from "@/components/Ctas";
import { IMAGES, SERVICES, HOME_STATS, JOURNEY_STEPS } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const HEADLINE_LINES = ["One Partner.", "Four Capabilities."];

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const contentFade = useTransform(scrollYProgress, [0, 0.9], [1, 0.25]);

    return (
        <div data-testid="home-page">
            <Seo
                title="Kavraj Spaces | Real Estate, Interiors & Furniture — New Delhi"
                description="One partner for real estate advisory, turnkey interior design and bespoke furniture in New Delhi. South Delhi property expertise, HNI & UHNI advisory, interiors and furniture across India."
            />

            <section
                ref={heroRef}
                className="relative flex min-h-[100svh] items-end overflow-hidden"
                data-testid="home-hero"
            >
                <motion.img
                    src={IMAGES.hero.url}
                    alt={IMAGES.hero.alt}
                    fetchpriority="high"
                    style={{ y: imgY }}
                    initial={{ scale: 1.12 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.8, ease: EASE }}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                    className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/30"
                    aria-hidden="true"
                />
                <motion.div
                    style={{ opacity: contentFade }}
                    className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-44 sm:px-12 lg:px-20 lg:pb-32"
                >
                    <motion.p
                        data-testid="hero-eyebrow"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                        className="inline-block border-y border-bronze-light/60 py-2.5 text-sm font-semibold uppercase tracking-[0.3em] text-bronze-light sm:text-base"
                    >
                        New Delhi — Real Estate · Interiors · Furniture
                    </motion.p>
                    <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.12] tracking-tight text-cream sm:text-5xl lg:text-6xl">
                        {HEADLINE_LINES.map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "112%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 1, delay: 0.45 + i * 0.16, ease: EASE }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-cream/80 sm:text-lg"
                    >
                        Kavraj Spaces brings together property advisory, turnkey interior design and
                        bespoke furniture manufacturing — one coordinated journey from the right
                        property to the finished space.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <DiscussButton testid="hero-cta-discuss" />
                        <ExploreButton testid="hero-cta-explore" />
                    </motion.div>
                </motion.div>
            </section>

            <section className="bg-cream" data-testid="home-intro">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-32">
                    <Reveal>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                            The Kavraj Spaces idea
                        </p>
                        <h2 className="mt-4 font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            From the right property to the finished space.
                        </h2>
                    </Reveal>
                    <Reveal delay={120}>
                        <p className="text-base leading-relaxed text-stone2 sm:text-lg">
                            Kavraj Spaces brings together the expertise of Marwaha Enterprises,
                            Kanwood Interiors, and an in-house furniture manufacturing division —
                            offering clients a coordinated experience, from identifying the right
                            property to designing, furnishing, and completing the space.
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-stone2">
                            One brief, one accountable team, and a standard of finish carried through
                            every stage.
                        </p>
                        <Link
                            to="/about"
                            data-testid="intro-link-about"
                            className="mt-7 inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-bronze transition-colors duration-300 hover:text-bronze-muted"
                        >
                            Our story
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <section
                id="capabilities"
                className="border-y border-bronze/15 bg-cream-muted"
                data-testid="home-capabilities"
            >
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                    <Reveal>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                            Four capabilities
                        </p>
                        <h2 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            Everything a space needs, under one roof.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6 xl:gap-8">
                        {SERVICES.map((service, i) => (
                            <Reveal key={service.id} delay={i * 120} className="h-full">
                                <Link
                                    to={service.link}
                                    data-testid={`service-card-${service.id}`}
                                    className="group flex h-full flex-col border border-bronze/15 bg-cream transition-[border-color,transform] duration-500 hover:-translate-y-1.5 hover:border-bronze/40"
                                >
                                    <div className="overflow-hidden">
                                        <img
                                            src={service.image.url}
                                            alt={service.image.alt}
                                            loading="lazy"
                                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col p-8">
                                        <span className="font-serif text-sm italic text-bronze">
                                            0{i + 1}
                                        </span>
                                        <h3 className="mt-2 font-serif text-2xl leading-snug text-ink">
                                            {service.title}
                                        </h3>
                                        <p className="mt-3 flex-1 text-sm leading-relaxed text-stone2">
                                            {service.summary}
                                        </p>
                                        <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-bronze transition-colors duration-300 group-hover:text-bronze-muted">
                                            Explore
                                            <ArrowUpRight
                                                size={15}
                                                aria-hidden="true"
                                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                            />
                                        </span>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <Marquee />

            <section className="bg-charcoal" data-testid="home-journey">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                    <Reveal>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze-light">
                            A connected journey
                        </p>
                        <h2 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-cream sm:text-4xl lg:text-5xl">
                            One brief. One team. Three stages.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
                        {JOURNEY_STEPS.map((step, i) => (
                            <Reveal key={step.number} delay={i * 120}>
                                <div
                                    className="border-t border-bronze/40 pt-6"
                                    data-testid={`journey-step-${step.number}`}
                                >
                                    <span className="font-serif text-4xl italic text-bronze-light">
                                        {step.number}
                                    </span>
                                    <h3 className="mt-4 font-serif text-2xl text-cream">{step.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-cream/70">
                                        {step.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-cream" data-testid="home-stats">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
                    <div className="grid grid-cols-1 gap-12 sm:grid-cols-3">
                        {HOME_STATS.map((stat, i) => (
                            <Reveal key={stat.label} delay={i * 120}>
                                <div className="border-l-2 border-bronze pl-6" data-testid={`stat-${i}`}>
                                    <p className="font-serif text-5xl tracking-tight text-ink lg:text-6xl">
                                        {stat.value}
                                    </p>
                                    <p className="mt-3 max-w-[16rem] text-sm leading-relaxed text-stone2">
                                        {stat.label}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={200}>
                        <p className="mt-14 max-w-2xl text-sm leading-relaxed text-stone2">
                            Our experience spans the aviation, hospitality, corporate, education,
                            healthcare and diplomatic sectors.{" "}
                            <Link
                                to="/experience"
                                data-testid="stats-link-experience"
                                className="font-semibold text-bronze transition-colors duration-300 hover:text-bronze-muted"
                            >
                                See our experience
                            </Link>
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="border-b border-bronze/15 bg-cream-muted" data-testid="home-breeze-master">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-32">
                    <Reveal>
                        <img
                            src={IMAGES.acCassette.url}
                            alt={IMAGES.acCassette.alt}
                            loading="lazy"
                            className="h-full w-full object-cover"
                        />
                    </Reveal>
                    <Reveal delay={120}>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                            Air Conditioning
                        </p>
                        <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            Breeze Master
                        </h2>
                        <p
                            data-testid="home-breeze-badge"
                            className="mt-5 inline-block border-y-2 border-bronze py-2 font-serif text-lg italic tracking-tight text-bronze sm:text-xl"
                        >
                            Wholesaler &amp; Retailer Since 1970
                        </p>
                        <p className="mt-6 text-base leading-relaxed text-stone2">
                            Our climate control arm — an authorised distributor and retailer for
                            leading air conditioning brands, supplying split, cassette, ductable and
                            VRV/VRF systems to homeowners, businesses, HVAC contractors and
                            resellers.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <Link
                                to="/air-conditioning"
                                data-testid="home-breeze-explore"
                                className="inline-flex items-center justify-center gap-2 bg-bronze px-7 py-3.5 text-sm font-semibold tracking-wide text-cream transition-[background-color,transform] duration-300 hover:bg-bronze-light active:scale-[0.97]"
                            >
                                Explore Air Conditioning
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="home-breeze-enquire"
                                className="inline-flex items-center justify-center gap-2 border border-charcoal/30 px-7 py-3.5 text-sm font-semibold tracking-wide text-charcoal transition-[color,border-color,transform] duration-300 hover:border-bronze hover:text-bronze active:scale-[0.97]"
                            >
                                Enquire Now
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand />
        </div>
    );
};

export default Home;
